/**
 * The error model: one body shape for every REST failure (and the WS `nack`'s
 * `error`), typed codes, and the one HTTP status each code maps to — so the
 * cloud and the node answer the same failure with the same status, and a
 * client branches on `code`, never on message text.
 */
import { z } from 'zod';

export const ERROR_CODES = [
  /** The request is malformed: not JSON, a bad path or query parameter. */
  'bad-request',
  /** Well-formed, but a value fails its schema or a domain rule; `issues` says which. */
  'validation-failed',
  /** No valid credential. */
  'unauthorized',
  /** Authenticated, but lacking the permission the route requires (named in `message`). */
  'forbidden',
  /** No such resource in the caller's tenant. */
  'not-found',
  /** The resource changed underneath the request (a create with an id that exists as something else). */
  'conflict',
  /** The body exceeds a limit (a patch batch above `MAX_PATCHES_PER_BATCH`). */
  'payload-too-large',
  /** Throttled per tenant (D13); retry after `retryAfterSeconds`. */
  'rate-limited',
  /** A server fault. Never a client's mistake. */
  'internal',
  /** The backend is temporarily unavailable; retry after `retryAfterSeconds`. */
  'unavailable',
  /** The caller's tenant is suspended by the vendor (phase 7b); `message` says why. Every authenticated route can answer it. */
  'suspended',
  /**
   * A signed node request (`ACP-Node`) from a node the cloud has revoked — replaced or removed in the portal
   * (D21). Its signature is good, so the cloud knows who it is and says so: retrying cannot help, the server
   * must be started again with a new enrolment code (docs/acp/sites.md). Every node route can answer it.
   */
  'node-revoked',
] as const;

export const errorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

/** The HTTP status for each code: the one mapping both hosts use. */
export const ERROR_HTTP_STATUS: Readonly<Record<ErrorCode, number>> = {
  'bad-request': 400,
  'validation-failed': 422,
  unauthorized: 401,
  forbidden: 403,
  'not-found': 404,
  conflict: 409,
  'payload-too-large': 413,
  'rate-limited': 429,
  internal: 500,
  unavailable: 503,
  suspended: 423,
  'node-revoked': 410,
};

export const errorIssueSchema = z
  .object({
    /** Where: a JSON path into the request (`patches.3.set.title`), or empty for the whole request. */
    path: z.string(),
    message: z.string(),
  })
  .strict();

export const errorResponseSchema = z
  .object({
    error: z
      .object({
        code: errorCodeSchema,
        /** Human-readable. Show it; do not parse it. */
        message: z.string(),
        issues: z.array(errorIssueSchema),
        /** Set on `rate-limited` and `unavailable`. */
        retryAfterSeconds: z.number().int().nonnegative().nullable(),
      })
      .strict(),
  })
  .strict();
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

/** Build an error body. */
export function errorResponse(code: ErrorCode, message: string, issues: readonly z.infer<typeof errorIssueSchema>[], retryAfterSeconds: number | null): ErrorResponse {
  return { error: { code, message, issues: [...issues], retryAfterSeconds } };
}

/** A zod failure as `validation-failed` issues. */
export function issuesFromZod(error: z.ZodError): z.infer<typeof errorIssueSchema>[] {
  return error.issues.map((issue) => ({ path: issue.path.map(String).join('.'), message: issue.message }));
}
